import {Injectable, inject} from "@angular/core";
import {HttpErrorResponse} from "@angular/common/http";
import {ComponentStore} from "@ngrx/component-store";
import {EMPTY, Observable} from "rxjs";
import {catchError, exhaustMap, switchMap, tap} from "rxjs/operators";
import {TrainVoucher, TrainVoucherPage} from "../models/train-voucher.model";
import {ThreePLTrainHttpService} from "../services/threepl-train-http.service";
import {TRAIN_VOUCHERS_PAGE_SIZE} from "../constants/threepl-train.constant";

export interface TrainVouchersQuery {
  from: number;
  to: number;
  active: boolean;
}

export interface TrainVouchersState {
  query: TrainVouchersQuery | undefined;
  items: TrainVoucher[];
  cursor: string;
  finished: boolean;
  loading: boolean;
  error: HttpErrorResponse | undefined;
}

const initialState: TrainVouchersState = {
  query: undefined, items: [], cursor: "", finished: false, loading: false, error: undefined,
};

@Injectable()
export class ThreePLTrainVouchersStore extends ComponentStore<TrainVouchersState> {
  private readonly http = inject(ThreePLTrainHttpService);

  readonly vm$ = this.select((s) => ({
    items: s.items,
    loading: s.loading,
    empty: !s.loading && !s.items.length,
    error: s.error,
  }));

  constructor() {
    super(initialState);
  }

  private readonly started = this.updater((state, query: TrainVouchersQuery): TrainVouchersState => ({
    ...initialState, query, loading: true,
  }));

  private readonly pageLoaded = this.updater((state, page: TrainVoucherPage): TrainVouchersState => ({
    ...state,
    items: [...state.items, ...page.list],
    cursor: page.cursor,
    finished: !page.cursor || page.list.length < TRAIN_VOUCHERS_PAGE_SIZE,
    loading: false,
  }));

  private readonly failed = this.updater((state, error: HttpErrorResponse): TrainVouchersState => ({
    ...state, loading: false, error,
  }));

  readonly load = this.effect((query$: Observable<TrainVouchersQuery>) =>
    query$.pipe(
      tap((query) => this.started(query)),
      switchMap((query) => this.fetch(query, undefined)),
    ),
  );

  readonly loadMore = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      exhaustMap(() => {
        const {query, cursor, finished, loading} = this.get();
        if (!query || finished || loading) {
          return EMPTY;
        }
        this.patchState({loading: true});
        return this.fetch(query, cursor);
      }),
    ),
  );

  private fetch(query: TrainVouchersQuery, cursor: string | undefined): Observable<TrainVoucherPage> {
    return this.http.list({...query, limit: TRAIN_VOUCHERS_PAGE_SIZE, cursor}).pipe(
      tap({
        next: (page) => this.get().query === query && this.pageLoaded(page),
        error: (error: HttpErrorResponse) => this.get().query === query && this.failed(error),
      }),
      catchError(() => EMPTY),
    );
  }
}
